import { createTheme } from "@mui/material/styles";
import { mediumFont, regularFont, theme } from "../../../utils/styles";

/**
 * Scoped MUI v5 theme for Astrocartography.
 *
 * The app mounts the legacy `@mui/styles` ThemeProvider, which does not feed
 * `@mui/material` v5 — so v5 components render against the default theme and
 * every screen restyles them inline. This theme is applied around this feature
 * only: component variants live here once, and the components below stay
 * semantic. Nothing outside this page is affected.
 *
 * Per-section tinting is driven by three CSS custom properties set on a section
 * root (`--astro-accent`, `--astro-accent-soft`, `--astro-accent-glow`), so the
 * overrides here stay colour-agnostic.
 */

export interface AstroAccent {
  main: string;
  soft: string;
  glow: string;
}

const makeAccent = (main: string, rgb: string): AstroAccent => ({
  main,
  soft: `rgba(${rgb}, 0.14)`,
  glow: `rgba(${rgb}, 0.42)`,
});

/** One celestial hue per control, so the dock reads as distinct instruments. */
export const ASTRO_ACCENTS = {
  profile: makeAccent("#9053F6", "144, 83, 246"),
  background: makeAccent("#06B6D4", "6, 182, 212"),
  cities: makeAccent("#F59E0B", "245, 158, 11"),
  results: makeAccent("#10B981", "16, 185, 129"),
  layers: makeAccent("#A78BFA", "167, 139, 250"),
  planets: makeAccent("#EC4899", "236, 72, 153"),
} as const;

/** Spreads an accent onto a component as CSS custom properties. */
export const accentVars = (accent: AstroAccent) => ({
  "--astro-accent": accent.main,
  "--astro-accent-soft": accent.soft,
  "--astro-accent-glow": accent.glow,
});

const SURFACE = "rgba(12, 10, 30, 0.72)";
const SURFACE_STRONG = "rgba(10, 8, 24, 0.86)";
const HAIRLINE = "rgba(255, 255, 255, 0.08)";
const TEXT_PRIMARY = "#FFFFFF";
const TEXT_SECONDARY = "rgba(255, 255, 255, 0.62)";
const TEXT_DISABLED = "rgba(255, 255, 255, 0.36)";

const baseAstroTheme = createTheme(theme, {
  palette: {
    mode: "dark",
    primary: { main: ASTRO_ACCENTS.profile.main },
    background: { paper: SURFACE, default: "#0a0818" },
    text: {
      primary: TEXT_PRIMARY,
      secondary: TEXT_SECONDARY,
      disabled: TEXT_DISABLED,
    },
    divider: HAIRLINE,
  },
  shape: { borderRadius: 14 },
  typography: {
    // Titles / labels use the medium weight, body copy the regular one.
    subtitle2: { ...mediumFont, fontSize: "13px", letterSpacing: "0.02em" },
    body2: { ...regularFont, fontSize: "13px" },
    caption: {
      ...regularFont,
      fontSize: "11px",
      lineHeight: 1.5,
      color: TEXT_DISABLED,
    },
    overline: {
      ...mediumFont,
      fontSize: "10px",
      letterSpacing: "0.1em",
      lineHeight: 1.6,
    },
  },
  components: {
    /* ------------------------------- surfaces ------------------------------ */
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          backgroundColor: SURFACE,
        },
      },
    },

    /* ------------------------------ accordions ----------------------------- */
    MuiAccordion: {
      // `square` avoids MUI's rounded variant, whose :first/:last-of-type rules
      // outrank the uniform radius below and would square off the outer cards.
      defaultProps: { disableGutters: true, square: true, elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: SURFACE,
          border: "1px solid var(--astro-accent-soft)",
          borderRadius: 14,
          overflow: "hidden",
          transition: "border-color 0.2s ease, box-shadow 0.2s ease",
          "&:hover": { borderColor: "var(--astro-accent-glow)" },
          "&.Mui-expanded": {
            borderColor: "var(--astro-accent-glow)",
            boxShadow: "0 6px 26px var(--astro-accent-soft)",
          },
          "&::before": { display: "none" },
          "& + &": { marginTop: 10 },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          minHeight: 56,
          padding: "0 12px",
          gap: 12,
          "&.Mui-expanded": { minHeight: 56 },
          "&:hover": { backgroundColor: "var(--astro-accent-soft)" },
          "&.Mui-expanded, &.Mui-focusVisible": {
            backgroundImage:
              "linear-gradient(135deg, var(--astro-accent-soft) 0%, transparent 100%)",
          },
        },
        content: {
          margin: "10px 0",
          gap: 12,
          alignItems: "center",
          minWidth: 0,
          "&.Mui-expanded": { margin: "10px 0" },
        },
        expandIconWrapper: { color: TEXT_DISABLED },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          // Top padding keeps the controls off the divider under the summary.
          padding: "14px 12px",
          borderTop: `1px solid ${HAIRLINE}`,
        },
      },
    },

    /* -------------------------------- lists -------------------------------- */
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 11,
          border: `1px solid ${HAIRLINE}`,
          gap: 10,
          paddingTop: 7,
          paddingBottom: 7,
          transition: "background-color 0.18s ease, border-color 0.18s ease",
          "&:hover": {
            backgroundColor: "var(--astro-accent-soft)",
            borderColor: "var(--astro-accent-glow)",
          },
          "&.Mui-selected": {
            backgroundColor: "var(--astro-accent-soft)",
            borderColor: "var(--astro-accent-glow)",
            "&:hover": { backgroundColor: "var(--astro-accent-soft)" },
          },
        },
      },
    },
    MuiListItemIcon: { styleOverrides: { root: { minWidth: 0 } } },
    MuiListItemText: {
      styleOverrides: {
        primary: { ...mediumFont, fontSize: "12.5px", color: TEXT_PRIMARY },
        secondary: { ...regularFont, fontSize: "10.5px", color: TEXT_DISABLED },
      },
    },

    /* -------------------------------- inputs ------------------------------- */
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          ...regularFont,
          fontSize: "13px",
          borderRadius: 12,
          backgroundColor: "rgba(255,255,255,0.04)",
          color: TEXT_PRIMARY,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "var(--astro-accent-soft)",
            transition: "border-color 0.2s ease",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "var(--astro-accent-glow)",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderWidth: 1,
            borderColor: "var(--astro-accent)",
          },
        },
        input: { "&::placeholder": { color: TEXT_DISABLED, opacity: 1 } },
      },
    },
    MuiSelect: {
      defaultProps: { size: "small" },
      styleOverrides: {
        select: { display: "flex", alignItems: "center", gap: 8 },
        icon: { color: TEXT_DISABLED },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          marginTop: 4,
          backgroundColor: "rgba(14, 11, 32, 0.97)",
          backdropFilter: "blur(18px)",
          border: "1px solid rgba(144,83,246,0.3)",
          boxShadow: "0 16px 40px rgba(0,0,0,0.6)",
        },
        list: { padding: 6 },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          ...regularFont,
          fontSize: "13px",
          borderRadius: 9,
          gap: 10,
          color: TEXT_SECONDARY,
          "&:hover": { backgroundColor: "rgba(144,83,246,0.18)" },
          "&.Mui-selected": {
            backgroundColor: "rgba(144,83,246,0.3)",
            color: TEXT_PRIMARY,
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          backgroundColor: "rgba(14, 11, 32, 0.97)",
          backdropFilter: "blur(18px)",
          border: "1px solid rgba(144,83,246,0.3)",
        },
        option: {
          ...regularFont,
          fontSize: "13px",
          borderRadius: 9,
          margin: "2px 6px",
          color: TEXT_SECONDARY,
          '&[aria-selected="true"]': {
            backgroundColor: "rgba(144,83,246,0.3)",
            color: TEXT_PRIMARY,
          },
          '&.Mui-focused, &[data-focus="true"]': {
            backgroundColor: "rgba(144,83,246,0.18)",
          },
        },
        noOptions: { ...regularFont, fontSize: "12px", color: TEXT_DISABLED },
        endAdornment: { "& .MuiSvgIcon-root": { color: TEXT_DISABLED } },
      },
    },

    /* ------------------------------- toggles ------------------------------- */
    MuiToggleButtonGroup: {
      styleOverrides: {
        grouped: {
          border: `1px solid ${HAIRLINE}`,
          borderRadius: "10px !important",
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          ...mediumFont,
          fontSize: "11px",
          letterSpacing: "0.04em",
          textTransform: "none",
          color: TEXT_SECONDARY,
          padding: "6px 4px",
          transition: "all 0.2s ease",
          "&:hover": {
            backgroundColor: "var(--astro-accent-soft)",
            borderColor: "var(--astro-accent-glow)",
            color: TEXT_PRIMARY,
          },
          "&.Mui-selected": {
            backgroundColor: "var(--astro-accent-soft)",
            borderColor: "var(--astro-accent)",
            color: TEXT_PRIMARY,
            boxShadow: "0 0 12px var(--astro-accent-soft)",
            "&:hover": { backgroundColor: "var(--astro-accent-soft)" },
          },
        },
      },
    },
    MuiSwitch: {
      defaultProps: { size: "small" },
      styleOverrides: {
        switchBase: {
          "&.Mui-checked": {
            color: "var(--astro-accent)",
            "& + .MuiSwitch-track": {
              backgroundColor: "var(--astro-accent)",
              opacity: 0.5,
            },
          },
        },
        track: { backgroundColor: "rgba(255,255,255,0.3)" },
      },
    },

    /* -------------------------------- chips -------------------------------- */
    MuiChip: {
      styleOverrides: {
        root: {
          ...mediumFont,
          fontSize: "11px",
          height: 26,
          borderRadius: 9,
          backgroundColor: "var(--astro-accent-soft)",
          border: "1px solid var(--astro-accent-glow)",
          color: "var(--astro-accent)",
        },
        label: { paddingLeft: 8, paddingRight: 8 },
        outlined: { backgroundColor: "transparent" },
      },
    },

    /* ------------------------------- feedback ------------------------------ */
    MuiSlider: {
      defaultProps: { size: "small" },
      styleOverrides: {
        root: { color: "var(--astro-accent)", height: 4 },
        rail: { backgroundColor: "rgba(255,255,255,0.16)", opacity: 1 },
        thumb: {
          width: 14,
          height: 14,
          backgroundColor: TEXT_PRIMARY,
          boxShadow: "0 0 0 2px var(--astro-accent)",
          "&:hover, &.Mui-focusVisible": {
            boxShadow: "0 0 0 2px var(--astro-accent), 0 0 0 7px var(--astro-accent-soft)",
          },
        },
        valueLabel: {
          ...mediumFont,
          fontSize: "11px",
          backgroundColor: "var(--astro-accent)",
          color: "#0a0818",
          borderRadius: 7,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { height: 3, borderRadius: 3, backgroundColor: "var(--astro-accent-soft)" },
        bar: { backgroundColor: "var(--astro-accent)", borderRadius: 3 },
      },
    },
    MuiTooltip: {
      defaultProps: { arrow: true, placement: "top" },
      styleOverrides: {
        tooltip: {
          ...regularFont,
          fontSize: "11px",
          backgroundColor: SURFACE_STRONG,
          border: `1px solid ${HAIRLINE}`,
          backdropFilter: "blur(10px)",
          padding: "6px 10px",
          borderRadius: 8,
        },
        arrow: { color: SURFACE_STRONG },
      },
    },

    /* -------------------------------- actions ------------------------------ */
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          ...mediumFont,
          fontSize: "12.5px",
          textTransform: "none",
          borderRadius: 11,
        },
        contained: {
          backgroundColor: "var(--astro-accent)",
          color: "#0a0818",
          "&:hover": {
            backgroundColor: "var(--astro-accent)",
            filter: "brightness(1.12)",
          },
          "&.Mui-disabled": {
            backgroundColor: "rgba(255,255,255,0.06)",
            color: TEXT_DISABLED,
          },
        },
        outlined: {
          borderColor: "var(--astro-accent-glow)",
          color: "var(--astro-accent)",
          "&:hover": {
            borderColor: "var(--astro-accent)",
            backgroundColor: "var(--astro-accent-soft)",
          },
        },
        text: {
          color: TEXT_SECONDARY,
          "&:hover": {
            color: TEXT_PRIMARY,
            backgroundColor: "var(--astro-accent-soft)",
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          color: TEXT_SECONDARY,
          transition: "all 0.2s ease",
          "&:hover": {
            color: TEXT_PRIMARY,
            backgroundColor: "var(--astro-accent-soft)",
          },
        },
      },
    },
    MuiDivider: { styleOverrides: { root: { borderColor: HAIRLINE } } },
  },
});

/**
 * Re-parents every portalled surface into `portalContainer`.
 *
 * A fullscreen element is promoted to the browser's top layer and only its own
 * subtree is rendered — anything portalled to `document.body` (MUI poppers,
 * menus, tooltips) sits outside that subtree and is therefore not painted at
 * all. It is not a stacking-order problem, so no z-index can rescue it; the
 * portal has to live inside the fullscreen element instead.
 *
 * Pass the fullscreen element while fullscreen is active and `null` otherwise —
 * MUI falls back to `document.body` for null, preserving normal behaviour.
 */
export const createAstroTheme = (portalContainer: HTMLElement | null) =>
  createTheme(baseAstroTheme, {
    components: {
      MuiPopover: { defaultProps: { container: portalContainer } },
      MuiMenu: { defaultProps: { container: portalContainer } },
      MuiTooltip: {
        defaultProps: { PopperProps: { container: portalContainer } },
      },
      MuiAutocomplete: {
        defaultProps: { slotProps: { popper: { container: portalContainer } } },
      },
    },
  });

export default baseAstroTheme;
